import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import {
  CreateReleaseRequest,
  Release,
  ReleaseMilestoneRequest,
  ReleaseStatus,
  ReleaseTestStageRequest,
  UpdateReleaseRequest,
} from '../models/release.model';
import { ReleaseAssessment, ReleaseDecisionRequest, SignoffRole } from '../models/release-assessment.model';

/**
 * Das aktuelle Release eines Projekts: das jüngste „In Abnahme“, sonst das jüngste
 * „In Test“, sonst das erste der Liste (Backend-Reihenfolge: neuester Stichtag, dann höhere Version).
 * Jüngstes = jüngster Stichtag im Status; hat dort keins einen, das erste dieses Status.
 */
export function pickCurrentRelease(releases: readonly Release[]): Release | null {
  return newestWithStatus(releases, 'IN_ABNAHME') ?? newestWithStatus(releases, 'IN_TEST') ?? releases[0] ?? null;
}

function newestWithStatus(releases: readonly Release[], status: ReleaseStatus): Release | null {
  const withStatus = releases.filter((r) => r.status === status);
  const dated = withStatus.filter((r) => !!r.stichtag);
  if (dated.length === 0) return withStatus[0] ?? null;
  return dated.reduce((newest, r) => ((r.stichtag ?? '') > (newest.stichtag ?? '') ? r : newest));
}

@Injectable({ providedIn: 'root' })
export class ReleaseService {
  private http = inject(HttpClient);
  private base = `${environment.apiBaseUrl}/api/v1/projects`;

  /** Liefert die Releases des Projekts, neueste (nach Stichtag) zuerst. */
  list(projectId: string): Observable<Release[]> {
    return this.http.get<Release[]>(`${this.base}/${projectId}/releases`);
  }

  /**
   * Ziel des Schnellstarts im Testfall: {@link pickCurrentRelease} nur über die nicht freigegebenen Releases,
   * damit ein Klick nie ungefragt einen Lauf an ein abgeschlossenes Release hängt; `null`, wenn keins bleibt.
   */
  currentRelease(projectId: string): Observable<Release | null> {
    return this.list(projectId).pipe(
      map((releases) => pickCurrentRelease(releases.filter((r) => r.status !== 'FREIGEGEBEN'))),
    );
  }

  getById(projectId: string, releaseId: string): Observable<Release> {
    return this.http.get<Release>(this.releaseUrl(projectId, releaseId));
  }

  create(projectId: string, body: CreateReleaseRequest): Observable<Release> {
    return this.http.post<Release>(`${this.base}/${projectId}/releases`, body);
  }

  update(projectId: string, releaseId: string, body: UpdateReleaseRequest): Observable<Release> {
    return this.http.put<Release>(this.releaseUrl(projectId, releaseId), body);
  }

  /** Ersetzt die Teststufen des Release in der gesendeten Reihenfolge. */
  speichereTeststufen(projectId: string, releaseId: string, stufen: ReleaseTestStageRequest[]): Observable<Release> {
    return this.http.put<Release>(`${this.releaseUrl(projectId, releaseId)}/test-stages`, stufen);
  }

  /** Ersetzt die Meilensteine in der gesendeten Reihenfolge; das Datum von Go-Live wird der Stichtag. */
  speichereMeilensteine(
    projectId: string,
    releaseId: string,
    meilensteine: ReleaseMilestoneRequest[],
  ): Observable<Release> {
    return this.http.put<Release>(`${this.releaseUrl(projectId, releaseId)}/milestones`, meilensteine);
  }

  getAssessment(projectId: string, releaseId: string): Observable<ReleaseAssessment> {
    return this.http.get<ReleaseAssessment>(`${this.releaseUrl(projectId, releaseId)}/assessment`);
  }

  saveDecision(projectId: string, releaseId: string, body: ReleaseDecisionRequest): Observable<ReleaseAssessment> {
    return this.http.put<ReleaseAssessment>(`${this.releaseUrl(projectId, releaseId)}/decision`, body);
  }

  confirmSignoff(
    projectId: string,
    releaseId: string,
    rolle: SignoffRole,
    version?: number,
  ): Observable<ReleaseAssessment> {
    return this.http.post<ReleaseAssessment>(
      `${this.releaseUrl(projectId, releaseId)}/signoffs/${encodeURIComponent(rolle)}/confirm`,
      null,
      version === undefined ? {} : { params: { version } },
    );
  }

  assignSignoff(
    projectId: string,
    releaseId: string,
    rolle: SignoffRole,
    userId: string | null,
  ): Observable<ReleaseAssessment> {
    return this.http.put<ReleaseAssessment>(
      `${this.releaseUrl(projectId, releaseId)}/signoff-assignments/${encodeURIComponent(rolle)}`,
      { userId },
    );
  }

  private releaseUrl(projectId: string, releaseId: string): string {
    return `${this.base}/${projectId}/releases/${encodeURIComponent(releaseId)}`;
  }
}
